import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService, { initProactiveAccessTokenRefresh } from '../../utils/apiService';
import { setLocalStorage, parseLocalStorage, removeLocalStorage, clearRememberedLogin } from '../../utils/localStorage';
import { clearMemoryAccessToken } from '../../utils/authUtils';
import { prepareAuthPayload, getFCMToken } from '../../utils/fcmUtils';
import { getDeviceInfo as getDetailedDeviceInfo } from '../../utils/platformDetection';
import { verifyOTP } from './otpSlice';
import { processPendingReferral } from '../../utils/referralTracking';
import { getLocationForAuth } from '../../utils/geoLocation';

// Async thunks for API calls
export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async (credentials, { rejectWithValue }) => {
        try {
            // Prepare login payload with FCM token
            const loginPayload = prepareAuthPayload(credentials);

            // Get pending tokens from session storage
            const pendingTokens = JSON.parse(sessionStorage.getItem('pending_tokens') || '[]');
            if (pendingTokens.length > 0) {
                loginPayload.pendingTokenAddresses = pendingTokens;
                // Clear pending tokens from session storage
                sessionStorage.removeItem('pending_tokens');
            }

            // Add user location to payload
            const location = getLocationForAuth();
            if (location) loginPayload.location = location;

            const response = await apiService.post('/auth/login', loginPayload, { requiresAuth: false });
            
            // Store user data in localStorage
            setLocalStorage('autobot_user', JSON.stringify(response.data));
            if (typeof window !== 'undefined') initProactiveAccessTokenRefresh();
            return response.data.user || response.data;
        } catch (error) {                               
            console.error('Login error:', error);
            
            // Get HTTP status code from error
            const httpStatus = error.status || null;
            
            // Check if error has validation details
            if (error.data?.details && Array.isArray(error.data.details)) {
                // Extract and join all validation messages
                const validationMessages = error.data.details.map(detail => detail.message).join(', ');
                const errorCode = error.data?.code || null;
                const lockedUntil = error.data?.lockedUntil || null;
                return rejectWithValue({ message: validationMessages, code: errorCode, lockedUntil, status: httpStatus });
            }
            
            // Extract the actual error message from the API response
            const errorMessage = error.message || error.data?.message || error.data?.error || 'Network error';
            const errorCode = error.data?.code || null;
            const lockedUntil = error.data?.lockedUntil || null;
            return rejectWithValue({ message: errorMessage, code: errorCode, lockedUntil, status: httpStatus });
        }
    }
);




export const loginWithGoogle = createAsyncThunk(
    'auth/loginWithGoogle',
    async (data, { rejectWithValue }) => {
        try {
            // Extract credential, sessionId, platform, and device from the data object
            const { credential, sessionId, platform, device, signupPage } = data;
            
            // Prepare payload with FCM token and device info
            const fcmToken = getFCMToken();
            const deviceInfo = getDetailedDeviceInfo();
            
            // Use provided platform/device or fall back to detection
            const finalPlatform = platform || deviceInfo.platform;
            const finalDevice = device || deviceInfo.device;
            
            // Get pending tokens from session storage
            const pendingTokens = JSON.parse(sessionStorage.getItem('pending_tokens') || '[]');
            
            const location = getLocationForAuth();
            const payload = {
                credential: credential, // Keep credential as string
                ...(sessionId && { sessionId }),
                ...(signupPage && { signupPage }),
                ...(fcmToken && { fcmToken }),
                device: finalDevice,
                platform: finalPlatform,
                ...(location && { location }),
                ...(pendingTokens.length > 0 && { pendingTokenAddresses: pendingTokens })
            };
            
            // Clear pending tokens from session storage if they exist
            if (pendingTokens.length > 0) {
                sessionStorage.removeItem('pending_tokens');
            }
            
            const response = await apiService.loginWithGoogle(payload);
            
            if (response.success) {
                // Store user data in localStorage
                setLocalStorage('autobot_user', JSON.stringify(response.data));
                if (typeof window !== 'undefined') initProactiveAccessTokenRefresh();
                return response.data.user || response.data;
            } else {
                return rejectWithValue({ message: response.message || 'Google login failed' });
            }
        } catch (error) {
            console.error('Google login error:', error);
            
            // Check if error has validation details
            if (error.data?.details && Array.isArray(error.data.details)) {
                // Extract and join all validation messages
                const validationMessages = error.data.details.map(detail => detail.message).join(', ');
                const errorCode = error.data?.code || null;
                return rejectWithValue({ message: validationMessages, code: errorCode });
            }
            
            // Extract the actual error message from the API response
            const errorMessage = error.message || error.data?.message || error.data?.error || 'Network error';
            const errorCode = error.data?.code || null;
            return rejectWithValue({ message: errorMessage, code: errorCode });
        }
    }
);

// Fixed initial state: never read localStorage here to avoid hydration mismatch
// (server has no localStorage → null; client may have user → different tree)
// Auth is rehydrated from localStorage in ReduxProvider after mount.
const initialState = {
    isAuthenticated: false,
    user: null,
    isLoading: false,
    error: null,
    errorCode: null,
    lockedUntil: null,
    httpStatus: null,
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        loginSuccess: (state, action) => {
            state.isAuthenticated = true;
            state.user = action.payload;
            state.error = null;
            state.errorCode = null;
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.error = null;
            state.errorCode = null;
            clearMemoryAccessToken();
            removeLocalStorage('autobot_user');
            clearRememberedLogin();
        },
        clearError: (state) => {
            state.error = null;
            state.errorCode = null;
            state.lockedUntil = null;
            state.httpStatus = null;
        },
        resetAuthState: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.error = null;
            state.isLoading = false;
            state.errorCode = null;
        },
        // Restore auth from localStorage after mount (client-only); avoids hydration mismatch
        rehydrateAuth: (state, action) => {
            if (action.payload && action.payload.user) {
                state.isAuthenticated = true;
                state.user = action.payload.user;
            }
        },
        mergeAuthUserProfile: (state, action) => {
            if (!state.user || !action.payload || typeof action.payload !== 'object') return;
            state.user = { ...state.user, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        builder
            // Login cases
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.errorCode = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.user = action.payload;
                state.error = null;
                state.errorCode = null;
                
                // Process pending referral code after successful login
                processPendingReferral().catch(err => {
                    console.error('Failed to process referral after login:', err);
                });
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                if (typeof action.payload === 'object') {
                    state.error = action.payload?.message || 'Login failed';
                    state.errorCode = action.payload?.code || null;
                    state.lockedUntil = action.payload?.lockedUntil || null;
                    state.httpStatus = action.payload?.status || null;
                } else {
                    state.error = action.payload;
                    state.errorCode = null;
                    state.lockedUntil = null;
                    state.httpStatus = null;
                }
            })
            // Google login cases
            .addCase(loginWithGoogle.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.errorCode = null;
            })
            .addCase(loginWithGoogle.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.user = action.payload;
                state.error = null;
                state.errorCode = null;
                
                // Process pending referral code after successful Google login
                processPendingReferral().catch(err => {
                    console.error('Failed to process referral after Google login:', err);
                });
            })
            .addCase(loginWithGoogle.rejected, (state, action) => {
                state.isLoading = false;
                if (typeof action.payload === 'object') {
                    state.error = action.payload?.message || 'Google login failed';
                    state.errorCode = action.payload?.code || null;
                } else {
                    state.error = action.payload;
                    state.errorCode = null;
                }
            })
            // Handle verifyOTP success when it includes registration (tokens)
            .addCase(verifyOTP.fulfilled, (state, action) => {
                // If the response includes tokens, it means registration was completed
                if (action.payload?.accessToken || action.payload?.token) {
                    state.isLoading = false;
                    state.isAuthenticated = true;
                    state.user = action.payload.user;
                    state.error = null;
                    state.errorCode = null;
                }
            });
    },
});

export const { loginSuccess, logout, clearError, resetAuthState, rehydrateAuth, mergeAuthUserProfile } = authSlice.actions;
export default authSlice.reducer;